/**
 * A provider's own device stock, and the devices loaded for a visit's residents (Isaiah Cotto, 2026-10-22;
 * docs/provider/PROVIDER_STOCK.md). Mirrors backend/src/routes/emergeProviderStock.js.
 *
 * A load is a PLAN ONLY ("load for tomorrow"): it never records a dispense, never changes ATD and never creates a
 * commission. The stock list and the visits ("In my car") carry no resident; only `residents` does (PHI).
 */
import { useCallback, useRef } from "react";
import { api } from "../../../services/api";

export type StockKind = "HEARING_AID" | "ACCESSORY" | "TOOL";
export type StockState = "IN_STOCK" | "ASSIGNED" | "DISPENSED" | "RETURNED" | "LOST";
export type Ear = "LEFT" | "RIGHT";

export interface StockItem {
  id: string;
  kind: StockKind;
  model: string | null;
  serial: string | null;
  ear: Ear | null;
  qty: number;
  state: StockState;
  origin: "PROVIDER" | "SHEET_IMPORT";
  version: number;
  created_at: string;
  updated_at: string;
  /** Where a loaded device is going: the visit's date and building, never the resident. */
  loaded_for: {
    load_id: string;
    visit_plan_id: string;
    planned_on: string | null;
    facility: { id: string; name: string | null };
    ear: Ear | null;
    /** False once the visit is completed, cancelled or past: release it back to stock. */
    plan_open: boolean;
  } | null;
}

export interface StockCounts {
  hearing_aids_in_stock: number;
  loaded: number;
  loaded_for_past_visits: number;
  accessories_and_tools: number;
}

export interface StockPage {
  linked: boolean;
  reason?: string;
  message?: string;
  items: StockItem[];
  closed: StockItem[];
  closed_truncated?: boolean;
  counts: StockCounts | null;
  today?: string;
}

export interface LoadedDevice {
  load_id: string;
  item_id: string;
  kind: StockKind;
  model: string | null;
  serial: string | null;
  ear: Ear | null;
  item_version: number;
}

export interface StockVisit {
  plan_id: string;
  date: string;
  state: string;
  arrival_time: string | null;
  route_order: number | null;
  facility: { id: string; name: string | null };
  loaded: LoadedDevice[];
}

export interface VisitsPage {
  linked: boolean;
  visits: StockVisit[];
  outside_scope: number;
  today?: string;
}

export interface VisitResident {
  patient_id: string;
  first_name: string | null;
  last_name: string | null;
  room_number: string | null;
  atd_approved: boolean;
  atd_added_date?: string | null;
  loaded: LoadedDevice[];
}

export interface VisitResidentsPage {
  visit: { plan_id: string; date: string; state: string; facility: { id: string; name: string | null }; open: boolean };
  access: "PROVIDER" | "OFFICE";
  can_load: boolean;
  residents: VisitResident[];
  not_care_related: number;
  max_per_resident: number;
}

export interface StockResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
  message?: string;
  /** Absent with no code: the server was never reached (services/api contract). */
  status?: number;
}

export interface NewItem {
  kind: StockKind;
  model?: string;
  serial?: string;
  ear?: Ear | "";
  qty?: number;
}

const BASE = "/emerge/provider-stock";
const withKey = (key: string) => ({ headers: { "Idempotency-Key": key } });
const id = (v: string) => encodeURIComponent(v);

/** One key per attempt: a retry with the same key is the same change (200 replayed), never a second one. */
export function newStockKey(): string {
  const c = typeof window !== "undefined" ? window.crypto : undefined;
  if (c && typeof c.randomUUID === "function") return `ps-${c.randomUUID()}`;
  return `ps-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * One Idempotency-Key PER ACTION (and per what the action carries: the count typed, the device chosen, the ear), kept
 * while that action's answer is unknown and dropped once the server answers it. A lost answer therefore retries the
 * same action with the same key (applied once), and never turns a DIFFERENT next action into a 409 key reuse.
 */
export function useActionKeys() {
  const keys = useRef<Record<string, string>>({});
  const keyFor = useCallback((action: string) => {
    if (!keys.current[action]) keys.current[action] = newStockKey();
    return keys.current[action];
  }, []);
  const settle = useCallback((action: string) => {
    delete keys.current[action];
  }, []);
  return { keyFor, settle };
}

/** True when the answer never arrived (or was lost): keep the key and let the provider try again. */
export const unanswered = (r: { status?: number; code?: string }) => r.status === undefined && !r.code;

export const providerStockApi = {
  list: async (): Promise<StockResult<StockPage>> => (await api.get(`${BASE}`)) as StockResult<StockPage>,

  add: async (item: NewItem, key: string): Promise<StockResult<{ item: StockItem; replayed: boolean }>> => {
    const body: Record<string, unknown> = { kind: item.kind };
    if (item.model) body.model = item.model;
    if (item.serial) body.serial = item.serial;
    if (item.ear) body.ear = item.ear;
    if (item.kind !== "HEARING_AID" && !item.serial && item.qty !== undefined) body.qty = item.qty;
    return (await api.post(`${BASE}/items`, body, withKey(key))) as StockResult<{ item: StockItem; replayed: boolean }>;
  },

  setQuantity: async (itemId: string, version: number, qty: number, key: string): Promise<StockResult<{ item: StockItem }>> =>
    (await api.post(`${BASE}/items/${id(itemId)}/quantity`, { version, qty }, withKey(key))) as StockResult<{ item: StockItem }>,

  mark: async (itemId: string, version: number, state: "LOST" | "RETURNED", key: string): Promise<StockResult<{ item: StockItem }>> =>
    (await api.post(`${BASE}/items/${id(itemId)}/mark`, { version, state }, withKey(key))) as StockResult<{ item: StockItem }>,

  release: async (itemId: string, version: number, key: string): Promise<StockResult<{ item: StockItem }>> =>
    (await api.post(`${BASE}/items/${id(itemId)}/release`, { version }, withKey(key))) as StockResult<{ item: StockItem }>,

  visits: async (facilityId?: string): Promise<StockResult<VisitsPage>> =>
    (await api.get(facilityId ? `${BASE}/visits?facility_id=${id(facilityId)}` : `${BASE}/visits`)) as StockResult<VisitsPage>,

  residents: async (planId: string): Promise<StockResult<VisitResidentsPage>> =>
    (await api.get(`${BASE}/visits/${id(planId)}/residents`)) as StockResult<VisitResidentsPage>,

  load: async (
    planId: string,
    body: { item_id: string; patient_id: string; ear?: Ear; version: number },
    key: string,
  ): Promise<StockResult<{ item: StockItem; replayed: boolean }>> =>
    (await api.post(`${BASE}/visits/${id(planId)}/loads`, body, withKey(key))) as StockResult<{ item: StockItem; replayed: boolean }>,
};

export const KIND_WORDS: Record<StockKind, string> = {
  HEARING_AID: "Hearing aid",
  ACCESSORY: "Accessory",
  TOOL: "Tool",
};

export const STATE_WORDS: Record<StockState, string> = {
  IN_STOCK: "In stock",
  ASSIGNED: "Loaded for a visit",
  DISPENSED: "Dispensed",
  RETURNED: "Returned",
  LOST: "Lost",
};

export function earWord(ear: Ear | null | undefined): string {
  if (ear === "LEFT") return "Left ear";
  if (ear === "RIGHT") return "Right ear";
  return "Ear not set";
}

export function shortDay(d: string | null | undefined): string {
  if (!d) return "date not recorded";
  const t = new Date(`${d}T12:00:00Z`);
  if (Number.isNaN(t.getTime())) return d;
  return t.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
}

/** One line naming a device: kind, model, serial, ear. */
export function deviceLine(d: { kind: StockKind; model: string | null; serial: string | null; ear: Ear | null }): string {
  return [KIND_WORDS[d.kind], d.model, d.serial ? `Serial ${d.serial}` : null, d.kind === "HEARING_AID" ? earWord(d.ear) : null]
    .filter(Boolean)
    .join(" · ");
}
